'use client'

import type { Fetchable } from '@morpho-org/blue-sdk'
import type { Address } from 'viem'

import { isDefined } from '@morpho-org/morpho-ts'
import { cn } from '@morpho-org/ui-v6/utils'
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from '@repo/ui'
import { type CSSProperties, type ReactNode, useState } from 'react'

import { useIsBelowBreakpoint } from '../../hooks/useMediaQuery'
import { Avatar, type AvatarSize, getAvatarSize } from './Avatar'

export interface DynamicAvatarsStackItem {
  hoverCardContent?: ReactNode
  logoUrl?: Fetchable<string> | Fetchable<string>[]
  /** Rendered in place of `logoUrl` when set. */
  icon?: ReactNode
  /** Draws this address's blockie instead of `logoUrl`. */
  address?: Address
  tooltipContent?: ReactNode
  /** Wraps the avatar in the consumer's own trigger, in place of the tooltip or hover card. */
  wrapTrigger?: (avatar: ReactNode) => ReactNode
}

export type DynamicAvatarsStackAlign = 'left' | 'right'
// | "center" | ;

export interface DynamicAvatarsStackProps {
  className?: string
  /** Kept for v5 API parity ; v5 never applied it to the root element */
  style?: CSSProperties
  items: DynamicAvatarsStackItem[]
  align?: DynamicAvatarsStackAlign
  // it is assumed that the extra info is an icon with 20px width
  extraInfo?: ReactNode
  maxTokensCount?: number
  avatarSize?: AvatarSize
  isMobileSupportEnabled?: boolean
  withHoverAnimation?: boolean
  /** Render each avatar with a circular mask (matches token-asset display). */
  rounded?: boolean
  /**
   * Trailing space reserved so siblings hold still while the stack fans out on hover.
   * Defaults to `avatarWidth + 4`. Only pass a value where the gap has been measured:
   * the hover transform shifts the stack 20px, so anything below that can touch a
   * flush sibling mid-hover.
   */
  trailingSpace?: number
  /** Wraps the "+N" marker in the consumer's own trigger, e.g. to list the hidden items. */
  wrapExtra?: (marker: ReactNode) => ReactNode
}

const GAP_WITH_EXTRA_INFO = 4

const PLUS_MARKER_BASE = 13
const PLUS_MARKER_DIGIT = 9

/** Negative margin each wrapper pulls back by, matching SquareWrapper's `-10px` classes. */
const AVATAR_OVERLAP = 10

/** Mobile: below the `md` breakpoint (768px). */
const MOBILE_BREAKPOINT = 768

/**
 * the width of the stack is calculated based on the number of items and the extra info
 * The aim is to avoid sibling elements to move when the stack grows
 */
const calculateWidth = (
  itemsLength: number,
  maxTokensCount: number,
  isExtraInfo: boolean,
  itemsToRender: DynamicAvatarsStackItem[],
  avatarSize: AvatarSize,
  trailingSpace: number | undefined
) => {
  const avatarWidth = getAvatarSize(avatarSize)

  // Reserve for the "+N" marker. Measured at text-xs with tabular figures: 17.2px for one
  // digit, +8.6px per extra digit, rounded up and padded to match the no-marker trailing gap.
  const overflowCount = itemsLength - maxTokensCount
  const plusMarker =
    overflowCount > 0 ? PLUS_MARKER_BASE + PLUS_MARKER_DIGIT * String(overflowCount).length : 0

  // Add the space for the extra info icon 20 + gap
  const extraInfoWidth = isExtraInfo ? 20 + GAP_WITH_EXTRA_INFO : 0

  // Find how many items have a double avatars stack
  const doubledStackNumber = itemsToRender.filter(
    item => Array.isArray(item.logoUrl) && item.logoUrl.length === 2
  ).length

  // the width of the wrapped avatar (avatar + negative margin)
  const wrappedAvatarSize = avatarWidth - AVATAR_OVERLAP

  // the width of the wrapped double avatar (double avatar + negative margin)
  const wrappedDoubleAvatarSize = avatarWidth * 2 - AVATAR_OVERLAP - AVATAR_OVERLAP

  // trailing reserve ; the trusted-by hero passes 20, measured to leave the same ~24px gap
  // with and without the "+N" marker. Every other consumer keeps the original reserve.
  const growSpace = trailingSpace ?? avatarWidth + 4

  return (
    growSpace +
    wrappedAvatarSize * (itemsToRender.length - doubledStackNumber) +
    wrappedDoubleAvatarSize * doubledStackNumber +
    plusMarker +
    extraInfoWidth
  )
}

interface SquareWrapperProps {
  /** 1-based position of this wrapper among the root's children (v5 `nth-of-type`) */
  position: number
  maxCount: number
  itemsCount: number
  hasExtraInfo: boolean
  direction: DynamicAvatarsStackAlign
  withHoverAnimation?: boolean
  children: ReactNode
}

/**
 * Tailwind replica of v5 `SquareWrapper`.
 *
 * The v5 Emotion styles relied on `nth-of-type` selectors whose indices were
 * interpolated at runtime ; since Tailwind classes must be statically
 * analyzable, the wrapper position is computed in JS instead (`position`) and
 * the position-dependent rules are applied conditionally:
 * - `&:nth-of-type(breakPosition) { margin: 2px }` -> `isBreak` margin classes
 * - `&:hover:nth-of-type(breakPosition) + div { 10px !important }` -> `isBreak` hover class
 * - v5's `&:hover:nth-of-type(breakPosition + 1) { 0 !important }` rule is dead
 *   code (it can never match an animated wrapper) and is intentionally dropped.
 */
function SquareWrapper({
  position,
  maxCount,
  itemsCount,
  hasExtraInfo,
  direction,
  withHoverAnimation,
  children
}: SquareWrapperProps) {
  const breakPosition = hasExtraInfo ? itemsCount : maxCount
  const isBreak = position === breakPosition
  const isLeft = direction === 'left'

  return (
    <div
      className={cn(
        'flex [transform:translate3d(0,0,0)]',
        itemsCount > 1 && [
          'will-change-transform [transition:transform_0.32s]',
          isBreak ? (isLeft ? 'mr-[2px]' : 'ml-[2px]') : isLeft ? '-mr-[10px]' : '-ml-[10px]'
        ],
        itemsCount > 1 &&
          withHoverAnimation &&
          (isLeft
            ? [
                '[&:hover:first-of-type~div]:[transform:translate3d(10px,0,0)]',
                '[&:hover:not(:first-of-type)]:[transform:translate3d(10px,0,0)]',
                '[&:hover:not(:first-of-type)~div]:[transform:translate3d(20px,0,0)]'
              ]
            : [
                '[&:hover:first-of-type~div]:[transform:translate3d(-10px,0,0)]',
                '[&:hover:not(:first-of-type)]:[transform:translate3d(-10px,0,0)]',
                '[&:hover:not(:first-of-type)~div]:[transform:translate3d(-20px,0,0)]'
              ]),
        itemsCount > 1 &&
          withHoverAnimation &&
          isBreak &&
          (isLeft
            ? '[&:hover+div]:[transform:translate3d(10px,0,0)]!'
            : '[&:hover+div]:[transform:translate3d(-10px,0,0)]!')
      )}
    >
      {children}
    </div>
  )
}

interface AvatarItemProps {
  className?: string
  item: DynamicAvatarsStackItem
  index: number
  maxTokensCount: number
  itemsToRender: DynamicAvatarsStackItem[]
  extraInfo: ReactNode
  align: DynamicAvatarsStackAlign
  size?: AvatarSize
  isMobileSupportEnabled?: boolean
  withHoverAnimation?: boolean
  rounded?: boolean
}

/**
 * Tailwind replica of v5 `AvatarItem`.
 */
function AvatarItem({
  item,
  className,
  index,
  maxTokensCount,
  itemsToRender,
  extraInfo,
  align,
  size = 'medium',
  isMobileSupportEnabled,
  withHoverAnimation = true,
  rounded = false
}: AvatarItemProps) {
  const [isOpen, setIsOpen] = useState(false)
  const isMobile = useIsBelowBreakpoint(MOBILE_BREAKPOINT)

  const triggerContent = item.icon ? (
    <Avatar icon={item.icon} size={size} rounded={rounded} />
  ) : item.address ? (
    <Avatar address={item.address} size={size} rounded={rounded} />
  ) : Array.isArray(item.logoUrl) ? (
    <div className={className}>
      {isDefined(item.logoUrl[0]) && <Avatar src={item.logoUrl[0]} size={size} rounded={rounded} />}
      {isDefined(item.logoUrl[1]) && (
        // v5 `StyledRightAvatar`: fixed -10px overlap regardless of size
        <Avatar src={item.logoUrl[1]} size={size} rounded={rounded} className="-ml-[10px]" />
      )}
    </div>
  ) : (
    <Avatar src={item.logoUrl} size={size} rounded={rounded} />
  )

  const renderContent = () => {
    if (item.wrapTrigger) return item.wrapTrigger(triggerContent)

    if (item.tooltipContent) {
      return (
        <Tooltip>
          <TooltipTrigger asChild>{triggerContent}</TooltipTrigger>
          <TooltipContent>{item.tooltipContent}</TooltipContent>
        </Tooltip>
      )
    }

    if (isMobileSupportEnabled && isMobile) {
      return (
        <Popover open={isOpen} onOpenChange={open => setIsOpen(open)}>
          <PopoverTrigger asChild>{triggerContent}</PopoverTrigger>
          {/* v5 `StyledPopoverContent`: padding 0, max-width 300px */}
          <PopoverContent side="top" sideOffset={8} className="max-w-[300px] rounded-lg p-0">
            {item.hoverCardContent}
          </PopoverContent>
        </Popover>
      )
    }

    if (item.hoverCardContent) {
      return (
        <HoverCard
          open={isOpen}
          onOpenChange={open => setIsOpen(open)}
          openDelay={200}
          closeDelay={100}
        >
          {item.hoverCardContent && (
            <HoverCardContent
              side="top"
              sideOffset={8}
              onClick={e => e.stopPropagation()}
              // `hoverCardContent` ships its own width + padding (e.g. HoverCardVaultContent is
              // `w-[300px] p-sm`); drop the shared shell's `w-72 p-4` so it isn't clipped/double-padded,
              // and restore the 20px brand radius the bespoke HoverCard.Content had (@repo/ui ships
              // `rounded-md`), matching HoverableCell.
              className="w-auto rounded-[20px] p-0"
              style={{ minWidth: '275px', maxWidth: '315px' }}
            >
              {item.hoverCardContent}
            </HoverCardContent>
          )}
          <HoverCardTrigger asChild>{triggerContent}</HoverCardTrigger>
        </HoverCard>
      )
    }

    return triggerContent
  }

  return (
    <SquareWrapper
      position={index + 1}
      maxCount={maxTokensCount}
      itemsCount={itemsToRender.length}
      hasExtraInfo={!!extraInfo}
      direction={align}
      withHoverAnimation={withHoverAnimation}
    >
      {renderContent()}
    </SquareWrapper>
  )
}

/**
 * DynamicAvatarsStack component - Tailwind replica of dapp-ui-v5 DynamicAvatarsStack
 *
 * Displays an overlapping stack of avatars with an optional "+N" marker and
 * extra info icon. Each avatar can reveal a tooltip or a hover card (popover
 * on mobile when `isMobileSupportEnabled`).
 *
 * @example
 * ```tsx
 * <DynamicAvatarsStack
 *   items={[{ logoUrl: "/eth.png", hoverCardContent: <div>ETH</div> }]}
 *   maxTokensCount={5}
 *   avatarSize="medium"
 * />
 * ```
 */
// TODO: Add support for center
export function DynamicAvatarsStack({
  className,
  items,
  align = 'left',
  extraInfo,
  maxTokensCount = 10,
  avatarSize = 'medium',
  isMobileSupportEnabled,
  withHoverAnimation = true,
  rounded = false,
  trailingSpace,
  wrapExtra
}: DynamicAvatarsStackProps) {
  const itemsLength = items.length

  const extraItemsCount = itemsLength - maxTokensCount
  const extraContent = extraItemsCount > 0 ? `+${extraItemsCount}` : ''

  const itemsToRender = items.slice(0, maxTokensCount)

  const orderedItemsToRender = align === 'right' ? [...itemsToRender].toReversed() : itemsToRender

  const width = calculateWidth(
    itemsLength,
    maxTokensCount,
    !!extraInfo,
    itemsToRender,
    avatarSize,
    trailingSpace
  )

  if (itemsLength === 0) {
    return null
  }

  return (
    <div
      className={cn(
        'flex items-center',
        align === 'left' ? 'flex-row' : 'flex-row-reverse',
        className
      )}
      style={{
        width
      }}
    >
      {orderedItemsToRender.map((item, index) => {
        return (
          <AvatarItem
            key={index}
            item={item}
            index={index}
            maxTokensCount={maxTokensCount}
            itemsToRender={itemsToRender}
            extraInfo={extraInfo}
            align={align}
            size={avatarSize}
            isMobileSupportEnabled={isMobileSupportEnabled}
            withHoverAnimation={withHoverAnimation}
            rounded={rounded}
          />
        )
      })}

      {(extraContent || extraInfo) && (
        <SquareWrapper
          position={itemsToRender.length + 1}
          maxCount={maxTokensCount}
          itemsCount={itemsToRender.length}
          hasExtraInfo={!!extraInfo}
          direction={align}
        >
          <div className="gap-2xs flex items-center">
            {extraContent &&
              (wrapExtra ?? (marker => marker))(
                <span className="text-foreground text-xs leading-normal tabular-nums">
                  {extraContent}
                </span>
              )}
            {extraInfo}
          </div>
        </SquareWrapper>
      )}
    </div>
  )
}
